import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardBody, CardHeader, Button, Input, Textarea, Alert, Modal } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';
import { parseDomains } from '../../utils/domains';

const CreateOrganizationPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [createdData, setCreatedData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    domains: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCopyPassword = async () => {
    if (!createdData?.generated_password) return;
    const ok = await copyToClipboard(createdData.generated_password);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy password');
    }
  };

  const handleDismissModal = () => {
    navigate(`/organizations/${createdData.id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const payload = { ...form, domains: parseDomains(form.domains) };
      // Strip empty admin fields so backend treats them as absent
      if (!payload.admin_email && !payload.admin_first_name && !payload.admin_last_name) {
        delete payload.admin_email;
        delete payload.admin_first_name;
        delete payload.admin_last_name;
      }

      const org = await organizationAPI.create(payload);

      if (logoFile && org?.id) {
        try {
          await organizationAPI.uploadLogo(org.id, logoFile);
        } catch {
          toast.warning('Organization created but logo upload failed');
        }
      }

      if (org?.admin_created && org?.generated_password) {
        setCreatedData(org);
      } else {
        toast.success('Organization created successfully');
        navigate(`/organizations/${org.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate('/organizations')}
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary transition-colors mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Organizations
          </button>
          <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white">
            Create Organization
          </h1>
        </div>

        {error && (
          <Alert variant="error" title="Error" className="mb-6">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Details</h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex items-center gap-4 mb-2">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="text-sm text-gray-500"
                    />
                  </div>
                </div>

                <Input
                  label="Organization Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Acme Capital"
                />
                <Input
                  label="Slug"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="Auto-generated if empty"
                  helperText="URL-friendly identifier. Leave blank to auto-generate from name."
                />
                <Textarea
                  label="Description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  maxLength={2000}
                  placeholder="Short profile or blurb about the organization (optional)"
                  helperText={`Optional. ${form.description.length}/2000 characters.`}
                />
                <Textarea
                  label="Domains"
                  name="domains"
                  value={form.domains}
                  onChange={handleChange}
                  rows={3}
                  placeholder={"portal.example.com\napp.example.com"}
                  helperText="One hostname per line. The login page on these domains shows this org's brand. A hostname can belong to only one org."
                />
              </div>
            </CardBody>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Admin User <span className="text-sm font-normal text-gray-400">(optional)</span></h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="admin_first_name"
                    value={form.admin_first_name}
                    onChange={handleChange}
                  />
                  <Input
                    label="Last Name"
                    name="admin_last_name"
                    value={form.admin_last_name}
                    onChange={handleChange}
                  />
                </div>
                <Input
                  label="Email"
                  name="admin_email"
                  type="email"
                  value={form.admin_email}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  If all three fields are filled, an admin user will be created and a temporary password shown once.
                </p>
              </div>
            </CardBody>
          </Card>

          <div className="flex items-center gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/organizations')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Organization'}
            </Button>
          </div>
        </form>
      </div>

      {createdData && (
        <Modal
          isOpen={true}
          onClose={null}
          title="Organization Created"
          showCloseButton={false}
          closeOnOverlayClick={false}
          footer={
            <Button onClick={handleDismissModal} className="w-full">
              {copied ? 'Copied — Continue' : 'Copy & Continue'}
            </Button>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{createdData.name}</span> has been created with an admin user.
            </p>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Admin email</span>
                <span className="font-medium text-gray-900 dark:text-white">{createdData.admin_email}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-gray-400">Password</span>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-gray-900 dark:text-white bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {createdData.generated_password}
                  </code>
                  <button
                    onClick={handleCopyPassword}
                    className="text-primary hover:text-primary/80 transition-colors"
                    title="Copy password"
                  >
                    {copied ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
              This password will not be shown again. Copy it before continuing.
            </p>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
};

export default CreateOrganizationPage;
